// src/components/TradeSelector/TradeSelector.tsx
import { loading, search } from "@simplybusiness/icons";
import { Combobox, Icon } from "@simplybusiness/mobius";
import { forwardRef, useCallback, useState } from "react";

// src/components/TradeSelector/TradeSelectorOption.tsx
import { Text } from "@simplybusiness/mobius";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
function TradeSelectorOption({
  option: { value, definition }
}) {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Text, { variant: "h4", children: value }),
    definition && /* @__PURE__ */ jsx(Text, { children: definition })
  ] });
}

// src/components/TradeSelector/constants.ts
var LABELS = {
  default: "Select a trade",
  ai: "Describe your trade"
};
var PLACEHOLDERS = {
  default: "e.g. plumber",
  ai: "e.g. I bake cakes"
};
var TRADES_ENDPOINTS = {
  production: "https://trade.api.simplybusiness.com/v1/trades",
  staging: "https://rambdas-staging.simplybusiness.co.uk/rambdas/trade-selector-chopin"
};
var DEFAULT_TRADES_ENDPOINT = false ? TRADES_ENDPOINTS.production : TRADES_ENDPOINTS.staging;

// src/components/TradeSelector/groupResults.ts
var groupResults = (results) => {
  const mapped = results.map(
    ({ value, vertical, definition }) => ({
      label: value,
      value,
      vertical,
      definition
    })
  );
  const { exact, suggested } = results.reduce(
    (groups, result, index) => {
      if (result.type === "exact") {
        groups.exact.push(mapped[index]);
      } else if (result.type === "suggested") {
        groups.suggested.push(mapped[index]);
      }
      return groups;
    },
    { exact: [], suggested: [] }
  );
  if (!suggested.length || !exact.length) {
    return mapped;
  }
  return [
    {
      heading: "Exact",
      options: exact
    },
    {
      heading: "Suggested",
      options: suggested
    }
  ];
};

// src/components/TradeSelector/makeTradesUrl.ts
function makeTradesUrl({
  site,
  vertical,
  query,
  tradesEndpoint,
  variant,
  searchScope
}) {
  const url = new URL(tradesEndpoint);
  url.searchParams.append("service_channel_identifier", site);
  url.searchParams.append("vertical", vertical);
  if (searchScope) {
    url.searchParams.append("search_scope", searchScope);
  }
  const usesShortParam = tradesEndpoint.toLowerCase().includes("rambdas");
  url.searchParams.append(usesShortParam ? "q" : "query", query);
  if (variant === "ai") {
    url.searchParams.append("ai_results", "true");
    url.searchParams.append("ai_strategy", "rag-strategy");
    url.searchParams.append("show_trade_definitions", "true");
  }
  return url.toString();
}

// src/components/TradeSelector/TradeSelector.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var TradeSelector = forwardRef(
  ({
    onSelectTrade,
    site,
    vertical,
    searchScope,
    tradesEndpoint = DEFAULT_TRADES_ENDPOINT,
    placeholder,
    variant = "default",
    ...otherProps
  }, ref) => {
    const [isFetching, setIsFetching] = useState(false);
    const fetchTrades = useCallback(
      (query) => {
        setIsFetching(true);
        const url = makeTradesUrl({
          site,
          vertical,
          searchScope,
          query,
          tradesEndpoint,
          variant
        });
        return fetch(url).then((response) => {
          if (!response.ok) {
            throw new Error("Network response was not ok");
          }
          return response.json();
        }).then((data) => data.results).then(groupResults).finally(() => {
          setIsFetching(false);
        });
      },
      [site, vertical, searchScope, tradesEndpoint, variant]
    );
    const searchPlaceholder = placeholder || PLACEHOLDERS[variant];
    const prefixIcon = isFetching ? /* @__PURE__ */ jsx2(Icon, { icon: loading, spin: true }) : /* @__PURE__ */ jsx2(Icon, { icon: search });
    return /* @__PURE__ */ jsx2(
      Combobox,
      {
        ref,
        label: LABELS[variant],
        placeholder: searchPlaceholder,
        asyncOptions: fetchTrades,
        onSelected: onSelectTrade,
        optionComponent: TradeSelectorOption,
        "data-variant": variant,
        icon: prefixIcon,
        ...otherProps
      }
    );
  }
);
TradeSelector.displayName = "TradeSelector";
export {
  TradeSelector,
  TradeSelectorOption
};
